import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CaseStudyCard = ({ caseStudy, onViewDetails }) => {
  const getIndustryIcon = (industry) => {
    const icons = {
      'Manufacturing': 'Factory',
      'Financial Services': 'Building2',
      'Healthcare': 'Heart',
      'Technology': 'Cpu',
      'Retail': 'ShoppingBag',
      'Energy': 'Zap'
    };
    return icons?.[industry] || 'Building';
  };

  const getSolutionTypeColor = (type) => {
    const colors = {
      'Computer Vision': 'bg-blue-100 text-blue-800',
      'NLP': 'bg-green-100 text-green-800',
      'Predictive Analytics': 'bg-purple-100 text-purple-800',
      'MLOps': 'bg-orange-100 text-orange-800',
      'Fraud Detection': 'bg-red-100 text-red-800',
      'Process Optimization': 'bg-indigo-100 text-indigo-800'
    };
    return colors?.[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg enterprise-shadow enterprise-border overflow-hidden enterprise-transition enterprise-hover group">
      {/* Header with Company Logo and Industry */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${caseStudy?.company === 'DIEXTRA App' ? 'bg-white' : 'bg-surface'}`}>
              <Image 
                src={caseStudy?.companyLogo} 
                alt={`${caseStudy?.company} logo`}
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{caseStudy?.company}</h3>
              <div className="flex items-center space-x-2 text-sm text-slate-600">
                <Icon name={getIndustryIcon(caseStudy?.industry)} size={16} />
                <span>{caseStudy?.industry}</span>
              </div>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSolutionTypeColor(caseStudy?.solutionType)}`}>
            {caseStudy?.solutionType}
          </span>
        </div>

        {/* Project Title and Description */}
        <h4 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-primary enterprise-transition">
          {caseStudy?.title}
        </h4>
        <p className="text-slate-700 text-sm leading-relaxed mb-4">
          {caseStudy?.description}
        </p>

        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          {caseStudy?.keyMetrics?.map((metric, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">
                {metric?.value}
              </div>
              <div className="text-xs text-slate-600">
                {metric?.label}
              </div>
            </div>
          ))}
        </div>

        {/* Technologies Used */}
        <div className="flex flex-wrap gap-2 mb-4">
          {caseStudy?.technologies?.slice(0, 4)?.map((tech, index) => (
            <span key={index} className="px-2 py-1 bg-surface text-text-secondary text-xs rounded-md">
              {tech}
            </span>
          ))}
          {caseStudy?.technologies?.length > 4 && (
            <span className="px-2 py-1 bg-surface text-text-secondary text-xs rounded-md">
              +{caseStudy?.technologies?.length - 4} more
            </span>
          )}
        </div>

        {/* Implementation Timeline */}
        <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} />
            <span>Timeline: {caseStudy?.timeline}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Users" size={16} />
            <span>Team: {caseStudy?.teamSize}</span>
          </div>
        </div>

        {/* ROI Highlight */}
        <div className="bg-success/10 border border-success/20 rounded-lg p-3 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-sm font-medium text-success">
              ROI: {caseStudy?.roi}
            </span>
          </div>
        </div>

        {/* Action Buttons removed per request */}
      </div>
      {/* Client Testimonial Preview removed per request */}
    </div>
  );
};

export default CaseStudyCard;